/**
 * API Client
 * 
 * Zentraler API-Client für alle Backend-Anfragen
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import apiConfig from '../config/api';
import logger from './logger';
import { storage } from './storage';

const MAX_429_RETRIES = 2;
const RETRY_DELAYS_MS = [2000, 5000]; // 2s, dann 5s

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Erweiterte Config für 429-Retry */
interface ConfigWithRetry extends AxiosRequestConfig {
  _retry429Count?: number;
}

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request-Interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Token hinzufügen
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }

        // Sprache bei allen Anfragen mitschicken (GET als Query-Param, alle als Header)
        const lang = storage.getItem('language') || 'de';
        if (config.method === 'get' || config.method === 'GET') {
          config.params = { ...config.params, lang };
        }
        config.headers['X-Language'] = lang;

        // Aktive Welt mitschicken
        const worldId = localStorage.getItem('ak_world_id') || 'world-1';
        config.headers['X-World-Id'] = worldId;

        logger.debug('API Request:', {
          method: config.method,
          url: config.url,
          data: config.data,
        });

        return config;
      },
      (error) => {
        logger.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response-Interceptor (inkl. 429-Retry mit Backoff)
    this.client.interceptors.response.use(
      (response) => {
        logger.debug('API Response:', {
          status: response.status,
          data: response.data,
        });
        return response;
      },
      async (error: AxiosError) => {
        const status = error.response?.status;
        const config = error.config as ConfigWithRetry | undefined;

        // 429 Too Many Requests: begrenzt erneut versuchen mit Wartezeit
        if (status === 429 && config && !config._retry429Count) {
          config._retry429Count = 0;
        }
        if (status === 429 && config && typeof config._retry429Count === 'number' && config._retry429Count < MAX_429_RETRIES) {
          const attempt = config._retry429Count;
          const delay = RETRY_DELAYS_MS[attempt] ?? 5000;
          config._retry429Count = attempt + 1;
          logger.warn(`429 erhalten – erneuter Versuch in ${delay / 1000}s (${attempt + 1}/${MAX_429_RETRIES})`, { url: config.url });
          await sleep(delay);
          return this.client.request(config);
        }

        // 401: Token abgelaufen ODER Request nach Logout (kein Token mehr vorhanden)
        if (status === 401) {
          const hadToken = !!this.token;
          const url = config?.url ?? '';
          // Polling-Endpunkte still ignorieren (kein Log, kein Logout)
          if (url.includes('unread-count')) {
            return Promise.reject(error);
          }
          this.clearToken();
          if (!hadToken) {
            // Kein Token → normales Verhalten nach Logout, kein Fehler-Log
            return Promise.reject(error);
          }
          // Token war gesetzt aber Server hat abgelehnt → Session abgelaufen
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          return Promise.reject(error);
        }

        logger.error('API Response Error:', {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data,
        });

        // Backend-Fehlermeldung extrahieren, damit der User den echten Grund sieht
        // Backend sendet entweder { error: "string" } oder { error: { message: "string" } }
        const responseData = error.response?.data as any;
        const backendMessage = responseData?.error?.message ?? (typeof responseData?.error === 'string' ? responseData.error : null) ?? responseData?.message;
        if (backendMessage) {
          const enrichedError = new Error(backendMessage);
          (enrichedError as any).status = status;
          (enrichedError as any).response = error.response;
          return Promise.reject(enrichedError);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Token setzen
   */
  setToken(token: string): void {
    this.token = token;
    storage.setItem('token', token);
  }

  /**
   * Token abrufen
   */
  getToken(): string | null {
    if (!this.token) {
      this.token = storage.getItem('token');
    }
    return this.token;
  }

  /**
   * Token löschen
   */
  clearToken(): void {
    this.token = null;
    storage.removeItem('token');
  }

  /**
   * GET-Request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  /**
   * POST-Request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  /**
   * PUT-Request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  /**
   * PATCH-Request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }

  /**
   * DELETE-Request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }
}

// Singleton-Instanz
const apiClient = new ApiClient();

export default apiClient;
